import { get, post, put, del } from '@/services/http';

export class DiscountModel {
  public id: number;
  public createdAt?: string;
  public updatedAt?: string;
  public code: string;
  public rate?: number;
  public limitIntervals: number;
  public activatedAt?: string;
  public status: string;
  public cancelledAt?: string;
  public merchantId: number | null;
  public kind: string;
  public limitUsageNumber: number;
  public appliedTime?: number;
  public merchantNames?: string[] | undefined;
  public type: string;
  public fixedDiscountAmount?: number;
  public fixedPrice?: number;
  public packageConfig?: any;

  constructor(data: any = {}) {
    this.id = data.id;
    this.code = data.code;
    this.rate = data.rate;
    this.limitIntervals = data.limitIntervals || 1;
    this.status = data.status || 'ACTIVE';
    this.merchantId = data.merchantId || null;
    this.kind = data.kind || 'REUSE';
    this.limitUsageNumber = data.limitUsageNumber || -1;
    this.type = data.type || 'PERCENTAGE_DISCOUNT';
    this.fixedDiscountAmount = data.fixedDiscountAmount;
    this.fixedPrice = data.fixedPrice;
    this.packageConfig = data.packageConfig;
  }

  public async fetch() {
    const res: any = await get(`/discounts/${this.id}`);
    this.mapData(res.data);
  }

  public async save() {
    this.cleanData();
    
    // Build payload based on discount type
    const payload: any = {
      limitIntervals: this.limitIntervals,
      status: 'ACTIVE',
      merchantId: this.merchantId,
      kind: this.kind,
      limitUsageNumber: this.limitUsageNumber,
      merchantNames: this.merchantNames,
      type: this.type,
    };

    // Only include packageConfig if it has actual values
    if (this.packageConfig && Object.keys(this.packageConfig).length > 0) {
      payload.packageConfig = this.packageConfig;
    }

    // Only include discount-related fields for actual discount types
    if (this.type !== 'NO_DISCOUNT') {
      payload.rate = this.rate;
      payload.discountAmount = this.fixedDiscountAmount;
      payload.fixedPrice = this.fixedPrice;
    }

    const res: any = await post('/discounts', payload);
    this.mapData(res.data);
  }

  public async update() {
    this.cleanData();
    
    // Build payload based on discount type
    const payload: any = {
      limitIntervals: this.limitIntervals,
      status: this.status,
      merchantId: this.merchantId,
      kind: this.kind,
      limitUsageNumber: this.limitUsageNumber,
      merchantNames: this.merchantNames,
      type: this.type,
    };

    // Only include packageConfig if it has actual values
    if (this.packageConfig && Object.keys(this.packageConfig).length > 0) {
      payload.packageConfig = this.packageConfig;
    }

    // Only include discount-related fields for actual discount types
    if (this.type !== 'NO_DISCOUNT') {
      payload.rate = this.rate;
      payload.discountAmount = this.fixedDiscountAmount;
      payload.fixedPrice = this.fixedPrice;
    }

    const res: any = await put(`/discounts/${this.id}`, payload);
  }

  public async del() {
    const res: any = await del(`/discounts/${this.id}`);
  }

  public mapData(data: any = {}) {
    this.id = data.id;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.code = data.code;
    this.rate = data.rate;
    this.limitIntervals = data.limitIntervals;
    this.activatedAt = data.activatedAt;
    this.status = data.status;
    this.cancelledAt = data.cancelledAt;
    this.merchantId = data.merchantId || null;
    this.kind = data.kind;
    this.limitUsageNumber = data.limitUsageNumber;
    this.appliedTime = data.appliedTime;
    this.type = data.type || 'PERCENTAGE_DISCOUNT';
    this.fixedDiscountAmount = data.discountAmount;
    this.fixedPrice = data.fixedPrice;
    // Handle both camelCase (from frontend) and snake_case (from database)
    this.packageConfig = data.packageConfig || data.package_config;
    if (data.merchantNames && data.merchantNames.length > 0) {
      this.merchantNames = data.merchantNames;
    }
  }

  private cleanData() {
    switch (this.type) {
      case 'NO_DISCOUNT':
        this.rate = undefined;
        this.fixedDiscountAmount = undefined;
        this.fixedPrice = undefined;
        // Set sensible defaults for NO_DISCOUNT
        this.limitIntervals = 1;
        this.limitUsageNumber = -1; // Unlimited usage
        break;
      case 'PERCENTAGE_DISCOUNT':
        this.fixedDiscountAmount = undefined;
        this.fixedPrice = undefined;
        break;
      case 'FIXED_DISCOUNT_AMOUNT':
        this.rate = undefined;
        this.fixedPrice = undefined;
        break;
      case 'FIXED_PRICE':
        this.rate = undefined;
        this.fixedDiscountAmount = undefined;
        break;
    }
  }
}
